import * as React from "react";
import { BusinessReportOutcome } from "@/modules/snapshots/businessReports.types";

export type SearchPhase = "initializing" | "thinking" | "retrieving" | "analyzing" | "complete";

interface UseSearchProgressParams {
  query: string;
  isThinkingComplete: boolean;
  documents: number;
  isProcessing: boolean;
  snapshotCollectionOutcomes: BusinessReportOutcome[] | undefined;
}

export interface PhaseTiming {
  duration: number; // in milliseconds
}

export interface SearchProgressMetrics {
  phase: SearchPhase;
  elapsedTime: number;
  documentCount: number;
  resultCount: number;
  progressPercentage: number; // 0-100 for retrieving phase
  phaseTimings: {
    thinking?: PhaseTiming;
    retrieving?: PhaseTiming;
    analyzing?: PhaseTiming;
  };
}

export const useSearchProgress = ({
  query,
  isThinkingComplete,
  documents,
  isProcessing,
  snapshotCollectionOutcomes,
}: UseSearchProgressParams): SearchProgressMetrics => {
  const [phaseStartTime, setPhaseStartTime] = React.useState<number>(0);
  const [elapsedTime, setElapsedTime] = React.useState<number>(0);
  const [phaseTimings, setPhaseTimings] = React.useState<SearchProgressMetrics["phaseTimings"]>({});

  const phase = React.useMemo<SearchPhase>(() => {
    if (!query) return "initializing";
    if (!isThinkingComplete) return "thinking";
    if (documents === undefined) return "retrieving";
    if (isProcessing) return "analyzing";
    return "complete";
  }, [query, isThinkingComplete, documents, isProcessing]);

  // Add fixed duration for retrieving phase if needed
  React.useEffect(() => {
    if (phase === "retrieving" && !phaseTimings.retrieving) {
      const fakeRetrieveDuration = 500; // 0.5 seconds
      setPhaseTimings(prev => ({
        ...prev,
        retrieving: { duration: fakeRetrieveDuration },
      }));
    }
  }, [phase, phaseTimings]);

  // Track phase transitions and calculate phase durations
  const prevPhaseRef = React.useRef<SearchPhase>("initializing");

  React.useEffect(() => {
    if (phase !== prevPhaseRef.current) {
      // Phase changed, record the duration of the previous phase
      const prevPhase = prevPhaseRef.current;
      if (phaseStartTime && prevPhase !== "initializing" && prevPhase !== "complete") {
        const duration = Date.now() - phaseStartTime;
        setPhaseTimings(prev => ({
          ...prev,
          [prevPhase]: { duration },
        }));
      }

      // Reset for new phase
      setPhaseStartTime(Date.now());
      setElapsedTime(0);
      prevPhaseRef.current = phase;
    }
  }, [phase, phaseStartTime]);

  // Track elapsed time for current phase
  React.useEffect(() => {
    if (phase === "initializing" || phase === "complete") {
      return;
    }

    const timer = setInterval(() => {
      const elapsed = Date.now() - phaseStartTime;
      setElapsedTime(elapsed);
    }, 100);

    return () => clearInterval(timer);
  }, [phase, phaseStartTime]);

  // Calculate progress percentage (for retrieving phase, estimate based on document count)
  const progressPercentage = React.useMemo(() => {
    if (phase === "retrieving") {
      // Estimate: assume typical searches find 50-200 documents
      // Show progress up to 100% at ~150 docs
      return Math.min(100, Math.round((documents / 150) * 100));
    }
    if (phase === "analyzing") {
      // Progress based on results vs documents
      const resultCount = snapshotCollectionOutcomes?.length || 0;

      // If no documents, return 100% if any results, else 0%
      if (documents === 0) {
        return resultCount > 0 ? 100 : 0;
      }

      return Math.min(100, Math.round((resultCount / documents) * 100));
    }
    return 0;
  }, [phase, documents, snapshotCollectionOutcomes?.length]);

  return {
    phase,
    elapsedTime,
    documentCount: documents,
    resultCount: snapshotCollectionOutcomes?.length || 0,
    progressPercentage,
    phaseTimings,
  };
};
